import React, { useState, useEffect, useMemo } from "react";
import { Select, Button, Checkbox, Spin, message, Input, Progress, Tooltip, Empty } from "antd";
import {
  SafetyCertificateOutlined, SaveOutlined, ReloadOutlined,
  SearchOutlined, UserOutlined, AppstoreOutlined,
  CheckCircleFilled, MinusCircleOutlined,
} from "@ant-design/icons";
import { apiRequest } from "../../../utilities/apiMethod";
import { useThemeStore } from "../../../store/useThemeStore";

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

/* ─── Tree visualization (right panel) ─────────────────────────────────────── */
function PermissionTree({ roleInfo, assignedGroups, totalChecked, themeColor }) {
  const rgb = hexToRgb(themeColor);

  if (!roleInfo) return null;

  if (totalChecked === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 14, padding: "40px 20px" }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: "#f4f5f7", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <SafetyCertificateOutlined style={{ fontSize: 26, color: "#d1d5db" }} />
        </div>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#9ca3af", textAlign: "center" }}>No permissions selected yet</p>
        <p style={{ margin: 0, fontSize: 12, color: "#c4c7cd", textAlign: "center" }}>Check menus on the left — they'll appear here as a tree</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 2px" }}>

      {/* ── Root node ─────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "12px 16px", borderRadius: 12, marginBottom: 0,
        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}cc 100%)`,
        boxShadow: `0 6px 20px ${themeColor}44`,
      }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.20)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <SafetyCertificateOutlined style={{ fontSize: 18, color: "#fff" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{roleInfo.RoleName}</p>
          <p style={{ margin: 0, fontSize: 11, color: "rgba(255,255,255,0.75)" }}>{totalChecked} permission{totalChecked !== 1 ? "s" : ""} assigned</p>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 900, color: "#fff", lineHeight: 1.1 }}>{totalChecked}</p>
          <p style={{ margin: 0, fontSize: 10, color: "rgba(255,255,255,0.70)" }}>menus</p>
        </div>
      </div>

      {/* ── Trunk + groups ────────────────────────────────────────────────── */}
      <div style={{ marginLeft: 22, borderLeft: `2px solid rgba(${rgb},0.25)`, paddingLeft: 0 }}>
        {assignedGroups.map((group, gi, arr) => {
          const isLast = gi === arr.length - 1;
          return (
            <div key={group.groupLabel} style={{ position: "relative", paddingLeft: 18, paddingTop: 10, paddingBottom: isLast ? 0 : 2 }}>

              {/* Horizontal branch to group ─────────────────────────────── */}
              <div style={{
                position: "absolute", left: 0, top: 24,
                width: 18, height: 2,
                background: `rgba(${rgb},0.30)`,
              }} />
              {/* Cut-off trunk at last item */}
              {isLast && (
                <div style={{
                  position: "absolute", left: -2, top: 24,
                  width: 2, bottom: 0,
                  background: "#fff",
                }} />
              )}

              {/* Group node ──────────────────────────────────────────────── */}
              <div style={{
                border: `1.5px solid rgba(${rgb},0.20)`,
                borderRadius: 10,
                background: `rgba(${rgb},0.05)`,
                overflow: "hidden",
              }}>
                {/* Group header */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 12px",
                  background: `rgba(${rgb},0.08)`,
                  borderBottom: `1px solid rgba(${rgb},0.12)`,
                }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: 6,
                    background: themeColor,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <AppstoreOutlined style={{ fontSize: 11, color: "#fff" }} />
                  </div>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {group.groupLabel}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 800, color: themeColor,
                    background: `rgba(${rgb},0.15)`,
                    padding: "2px 8px", borderRadius: 20, flexShrink: 0,
                  }}>
                    {group.items.length}
                  </span>
                </div>

                {/* Menu leaf nodes ──────────────────────────────────────── */}
                <div style={{ padding: "8px 10px", display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {group.items.map((item, mi, marr) => {
                    const isLastMenu = mi === marr.length - 1;
                    return (
                      <div key={item.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <div style={{ width: 5, height: 5, borderRadius: "50%", background: `rgba(${rgb},0.50)`, flexShrink: 0 }} />
                        <span style={{
                          fontSize: 11.5, fontWeight: 500, color: "#374151",
                          background: "#fff",
                          border: `1px solid rgba(${rgb},0.20)`,
                          padding: "3px 9px", borderRadius: 20,
                          whiteSpace: "nowrap",
                        }}>
                          {item.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────────────────────── */
const RolePermission = () => {
  const { themeColor } = useThemeStore();
  const [messageApi, contextHolder] = message.useMessage();

  const [roleData,       setRoleData]       = useState([]);
  const [selectedRole,   setSelectedRole]   = useState(null);
  const [permissionData, setPermissionData] = useState([]);
  const [checkedKeys,    setCheckedKeys]    = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [submitting,     setSubmitting]     = useState(false);
  const [error,          setError]          = useState("");
  const [search,         setSearch]         = useState("");

  useEffect(() => { fetchRoleData(); }, []);

  async function fetchRoleData() {
    setLoading(true);
    try {
      const val = await apiRequest("GET", "Roles");
      setRoleData(val?.IsSuccess && Array.isArray(val.Data) ? val.Data : []);
    } catch { messageApi.error("Failed to load roles"); }
    finally  { setLoading(false); }
  }

  async function fetchRolePermissions(roleId) {
    setLoading(true);
    try {
      const res = await apiRequest("GET", `RolePermissions/getrolepermission/${roleId}`);
      if (res?.IsSuccess && res.Data) {
        setPermissionData(res.Data);
        setCheckedKeys(extractCheckedKeys(res.Data));
      } else { setPermissionData([]); setCheckedKeys([]); }
    } catch { messageApi.error("Failed to load permissions"); }
    finally  { setLoading(false); }
  }

  function extractCheckedKeys(data) {
    const keys = [];
    data.forEach(item => {
      if (!item.IsChildMenu && item.AccessAllowed && item.MenuId)
        keys.push(`menu-${item.MenuId}`);
      if (item.IsChildMenu && item.Parent?.Child)
        item.Parent.Child.forEach(c => { if (c.AccessAllowed && c.MenuId) keys.push(`menu-${c.MenuId}`); });
    });
    return keys;
  }

  /* ── All menu items flat list ──────────────────────────────────────────── */
  const allMenuItems = useMemo(() => {
    const items = [];
    const seenGroups = new Set();
    permissionData.forEach(d => {
      if (!d.IsChildMenu && d.MenuId) {
        items.push({ key: `menu-${d.MenuId}`, label: d.Menu, group: "_standalone", groupLabel: "Other Menus" });
      }
      if (d.IsChildMenu && d.Parent?.Child && !seenGroups.has(d.MenuGroup)) {
        seenGroups.add(d.MenuGroup);
        d.Parent.Child.forEach(c => {
          items.push({ key: `menu-${c.MenuId}`, label: c.Menu, group: `g_${d.MenuGroup}`, groupLabel: d.Parent.Menu });
        });
      }
    });
    return items;
  }, [permissionData]);

  /* ── Grouped structure ──────────────────────────────────────────────────── */
  const allGroups = useMemo(() => {
    const map = new Map();
    allMenuItems.forEach(item => {
      if (!map.has(item.group)) map.set(item.group, { groupLabel: item.groupLabel, items: [] });
      map.get(item.group).items.push(item);
    });
    return [...map.values()];
  }, [allMenuItems]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allGroups;
    return allGroups.map(g => ({
      ...g,
      items: g.items.filter(i => i.label.toLowerCase().includes(q) || g.groupLabel.toLowerCase().includes(q)),
    })).filter(g => g.items.length > 0);
  }, [allGroups, search]);

  const assignedGroups = useMemo(() => {
    const map = new Map();
    allMenuItems.filter(m => checkedKeys.includes(m.key)).forEach(item => {
      if (!map.has(item.group)) map.set(item.group, { groupLabel: item.groupLabel, items: [] });
      map.get(item.group).items.push(item);
    });
    return [...map.values()];
  }, [allMenuItems, checkedKeys]);

  const totalMenus   = allMenuItems.length;
  const totalChecked = checkedKeys.filter(k => k.startsWith("menu-")).length;
  const progressPct  = totalMenus > 0 ? Math.round((totalChecked / totalMenus) * 100) : 0;
  const selectedRoleInfo = useMemo(() => roleData.find(r => r.RoleId === selectedRole), [roleData, selectedRole]);

  /* ── Handlers ───────────────────────────────────────────────────────────── */
  function handleRoleChange(roleId) {
    setSelectedRole(roleId); setError(""); setSearch("");
    if (roleId) fetchRolePermissions(roleId);
    else { setPermissionData([]); setCheckedKeys([]); }
  }

  function toggleMenu(key) {
    setCheckedKeys(p => p.includes(key) ? p.filter(k => k !== key) : [...p, key]);
  }

  function toggleGroup(groupItems, checked) {
    const keys = groupItems.map(i => i.key);
    if (checked) setCheckedKeys(p => [...new Set([...p, ...keys])]);
    else         setCheckedKeys(p => p.filter(k => !keys.includes(k)));
  }

  function handleSelectAll()   { setCheckedKeys(allMenuItems.map(m => m.key)); }
  function handleDeselectAll() { setCheckedKeys([]); }

  function handleReset() {
    setSelectedRole(null); setPermissionData([]); setCheckedKeys([]);
    setError(""); setSearch("");
  }

  async function handleSubmit() {
    if (!selectedRole) { setError("Please select a role"); return; }
    if (totalChecked === 0) { messageApi.warning("Assign at least one permission"); return; }
    setSubmitting(true);
    try {
      const payload = checkedKeys
        .filter(k => k.startsWith("menu-"))
        .map(k => ({ roleId: selectedRole, menuId: parseInt(k.replace("menu-", "")) }));
      const res = await apiRequest("POST", "RolePermissions", payload);
      if (res?.IsSuccess) { messageApi.success("Permissions saved!"); handleReset(); }
      else {
        const err = res?.ErrorMessage?.length ? res.ErrorMessage.join(", ") : res?.DisplayMessage || "Save failed";
        messageApi.error(err);
      }
    } catch { messageApi.error("Error saving permissions"); }
    finally  { setSubmitting(false); }
  }

  const rgb = hexToRgb(themeColor);

  /* ── Page ───────────────────────────────────────────────────────────────── */
  return (
    <div style={{ padding: "4px 0", display: "flex", flexDirection: "column", gap: 10 }}>
      {contextHolder}

      {/* ── Compact header row ──────────────────────────────────────────── */}
      <div style={{
        background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3",
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)", padding: "10px 16px",
        display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
      }}>
        {/* Icon + title */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "0 0 auto" }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9, background: themeColor,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 3px 10px ${themeColor}44`, flexShrink: 0, transition: "background 0.3s",
          }}>
            <SafetyCertificateOutlined style={{ fontSize: 16, color: "#fff" }} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#111827" }}>Role Permissions</p>
            <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>Assign menu access per role</p>
          </div>
        </div>

        {/* Role selector */}
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <Select
            placeholder="Select a role…"
            value={selectedRole}
            onChange={handleRoleChange}
            options={roleData.map(r => ({ value: r.RoleId, label: r.RoleName }))}
            allowClear showSearch optionFilterProp="label"
            loading={loading && !selectedRole}
            style={{ width: "100%", height: 36 }}
            status={error ? "error" : ""}
          />
          {error && <span style={{ fontSize: 11, color: "#dc2626" }}>{error}</span>}
        </div>

        {/* Role info badge */}
        {selectedRoleInfo && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto",
            padding: "5px 12px", borderRadius: 8,
            background: `rgba(${rgb},0.07)`, border: `1px solid rgba(${rgb},0.18)`,
          }}>
            <UserOutlined style={{ color: themeColor, fontSize: 13 }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{selectedRoleInfo.RoleName}</span>
            <span style={{
              fontSize: 11, fontWeight: 800, color: themeColor,
              background: `rgba(${rgb},0.15)`, padding: "1px 7px", borderRadius: 20,
            }}>
              {totalChecked}/{totalMenus}
            </span>
          </div>
        )}

        {/* Actions */}
        {selectedRole && (
          <div style={{ display: "flex", gap: 7, flex: "0 0 auto" }}>
            <Button
              size="small" icon={<ReloadOutlined />} onClick={handleReset}
              style={{ borderRadius: 8, height: 34, fontWeight: 600, borderColor: "#e5e7eb", color: "#6b7280", fontSize: 12 }}
            >
              Reset
            </Button>
            <Button
              size="small" icon={<SaveOutlined />} onClick={handleSubmit} loading={submitting}
              disabled={totalChecked === 0}
              style={{
                borderRadius: 8, height: 34, fontWeight: 700, fontSize: 12,
                background: totalChecked > 0 ? themeColor : undefined,
                borderColor: totalChecked > 0 ? themeColor : undefined,
                color: totalChecked > 0 ? "#fff" : undefined,
                boxShadow: totalChecked > 0 ? `0 2px 8px ${themeColor}44` : undefined,
                transition: "all 0.3s",
              }}
            >
              Save
            </Button>
          </div>
        )}
      </div>

      {/* ── Progress bar (only when role selected) ──────────────────────── */}
      {selectedRole && !loading && totalMenus > 0 && (
        <div style={{
          background: "#fff", borderRadius: 10, border: "1px solid #f0f1f3",
          boxShadow: "0 1px 4px rgba(0,0,0,0.03)", padding: "8px 16px",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", whiteSpace: "nowrap" }}>
            Permissions
          </span>
          <Progress
            percent={progressPct} showInfo={false}
            strokeColor={progressPct === 100 ? "#16a34a" : themeColor}
            trailColor="#f0f1f3" strokeWidth={5}
            style={{ flex: 1, margin: 0 }}
          />
          <span style={{ fontSize: 11, fontWeight: 800, color: progressPct === 100 ? "#16a34a" : themeColor, whiteSpace: "nowrap" }}>
            {totalChecked}/{totalMenus} · {progressPct}%
          </span>
        </div>
      )}

      {/* ── No role state ────────────────────────────────────────────────── */}
      {!selectedRole && (
        <div style={{
          background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3",
          boxShadow: "0 1px 6px rgba(0,0,0,0.04)", padding: "50px 24px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
        }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: `rgba(${rgb},0.08)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SafetyCertificateOutlined style={{ fontSize: 26, color: themeColor }} />
          </div>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>Select a Role to Begin</p>
          <p style={{ margin: 0, fontSize: 12, color: "#9ca3af", textAlign: "center", maxWidth: 320 }}>
            Choose a role from the dropdown above, then assign menu permissions from the left panel.
          </p>
        </div>
      )}

      {/* ── Loading ──────────────────────────────────────────────────────── */}
      {selectedRole && loading && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3", padding: "50px", display: "flex", justifyContent: "center", alignItems: "center", gap: 12 }}>
          <Spin size="large" />
          <span style={{ fontSize: 13, color: "#9ca3af" }}>Loading permissions…</span>
        </div>
      )}

      {/* ── Two-column layout ────────────────────────────────────────────── */}
      {selectedRole && !loading && (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>

          {/* ── LEFT: Selection panel ─────────────────────────────────── */}
          <div style={{
            flex: "0 0 42%", background: "#fff", borderRadius: 12,
            border: "1px solid #f0f1f3", boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}>
            {/* Left header */}
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #f0f1f3", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>
                  Available Menus <span style={{ color: "#9ca3af", fontWeight: 500 }}>({allMenuItems.length})</span>
                </span>
                <div style={{ display: "flex", gap: 5 }}>
                  <button
                    onClick={handleSelectAll}
                    style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6, border: "none", cursor: "pointer", background: `rgba(${rgb},0.10)`, color: themeColor }}
                  >
                    All
                  </button>
                  <button
                    onClick={handleDeselectAll}
                    style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6, border: "none", cursor: "pointer", background: "#fff1f2", color: "#ef4444" }}
                  >
                    None
                  </button>
                </div>
              </div>
              <Input
                placeholder="Search menus…"
                prefix={<SearchOutlined style={{ color: "#c4c7cd", fontSize: 11 }} />}
                value={search} onChange={e => setSearch(e.target.value)}
                allowClear size="small"
                style={{ borderRadius: 7, fontSize: 12 }}
              />
            </div>

            {/* Left body - grouped checkboxes */}
            <div style={{ overflowY: "auto", maxHeight: 500, padding: "8px 10px" }}>
              {filteredGroups.length === 0
                ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No menus found" style={{ padding: "30px 0" }} />
                : filteredGroups.map(group => {
                    const groupChecked  = group.items.filter(i => checkedKeys.includes(i.key)).length;
                    const allOn         = groupChecked === group.items.length;
                    const someOn        = groupChecked > 0 && groupChecked < group.items.length;
                    return (
                      <div key={group.groupLabel} style={{ marginBottom: 10 }}>
                        {/* Group header */}
                        <div style={{
                          display: "flex", alignItems: "center", gap: 7,
                          padding: "5px 8px", borderRadius: 7,
                          background: `rgba(${rgb},0.05)`,
                          borderLeft: `3px solid ${allOn ? themeColor : someOn ? themeColor + "80" : "#e5e7eb"}`,
                          marginBottom: 3,
                        }}>
                          <Checkbox
                            checked={allOn}
                            indeterminate={someOn}
                            onChange={e => toggleGroup(group.items, e.target.checked)}
                          />
                          <span style={{ flex: 1, fontSize: 11.5, fontWeight: 700, color: "#374151" }}>{group.groupLabel}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 700,
                            color: groupChecked > 0 ? themeColor : "#9ca3af",
                          }}>
                            {groupChecked}/{group.items.length}
                          </span>
                        </div>
                        {/* Menu items */}
                        {group.items.map(item => (
                          <div
                            key={item.key}
                            onClick={() => toggleMenu(item.key)}
                            style={{
                              display: "flex", alignItems: "center", gap: 8,
                              padding: "5px 8px 5px 18px", borderRadius: 7,
                              cursor: "pointer", marginBottom: 1,
                              background: checkedKeys.includes(item.key) ? `rgba(${rgb},0.06)` : "transparent",
                              transition: "background 0.12s",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = `rgba(${rgb},0.09)`; }}
                            onMouseLeave={e => { e.currentTarget.style.background = checkedKeys.includes(item.key) ? `rgba(${rgb},0.06)` : "transparent"; }}
                          >
                            <Checkbox
                              checked={checkedKeys.includes(item.key)}
                              onChange={() => toggleMenu(item.key)}
                              onClick={e => e.stopPropagation()}
                            />
                            <span style={{
                              flex: 1, fontSize: 12, color: checkedKeys.includes(item.key) ? "#111827" : "#6b7280",
                              fontWeight: checkedKeys.includes(item.key) ? 600 : 400,
                              transition: "all 0.12s",
                            }}>
                              {item.label}
                            </span>
                            {checkedKeys.includes(item.key) && (
                              <CheckCircleFilled style={{ fontSize: 12, color: themeColor }} />
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })
              }
            </div>
          </div>

          {/* ── RIGHT: Tree graph ─────────────────────────────────────── */}
          <div style={{
            flex: 1, background: "#fff", borderRadius: 12,
            border: "1px solid #f0f1f3", boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}>
            {/* Right header */}
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #f0f1f3", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>
                Permission Tree
              </span>
              {totalChecked > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 800, color: themeColor,
                  background: `rgba(${rgb},0.12)`, padding: "2px 10px", borderRadius: 20,
                }}>
                  {totalChecked} assigned
                </span>
              )}
            </div>

            {/* Right body - tree visualization */}
            <div style={{ overflowY: "auto", maxHeight: 500, padding: "12px 14px" }}>
              <PermissionTree
                roleInfo={selectedRoleInfo}
                assignedGroups={assignedGroups}
                totalChecked={totalChecked}
                themeColor={themeColor}
              />
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default RolePermission;
